<template>
  <!-- Mobile drawer backdrop -->
  <transition name="fade">
    <div
      v-if="ui.isMobile && ui.mobileNavOpen"
      class="sidebar-backdrop"
      @click="ui.toggleMobileNav"
    />
  </transition>

  <!-- Sidebar -->
  <transition :name="ui.isMobile ? 'slide-left' : ''">
    <aside
      v-show="ui.isDesktop || ui.mobileNavOpen"
      class="sidebar"
      :class="{
        'sidebar--collapsed': ui.isDesktop && !ui.sidebarOpen,
        'sidebar--mobile': ui.isMobile,
      }"
      @click="onSidebarClick"
    >
      <div class="sidebar-header">
        <router-link to="/" class="sidebar-brand">
          <Logo :size="32" />
          <span class="sidebar-title" v-show="ui.isDesktop || ui.mobileNavOpen">Wiki</span>
        </router-link>
      </div>

      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item" exact-active-class="nav-item--active">
          <span class="nav-icon">📄</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Recent Pages</span>
        </router-link>
        <router-link to="/search" class="nav-item" active-class="nav-item--active">
          <span class="nav-icon">🔍</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Search</span>
        </router-link>
        <router-link to="/workbench" class="nav-item" active-class="nav-item--active">
          <span class="nav-icon">🤖</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Workbench</span>
        </router-link>
        <router-link to="/profile" class="nav-item" active-class="nav-item--active">
          <span class="nav-icon">👤</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Profile</span>
        </router-link>
        <router-link v-if="auth.isAdmin" to="/settings" class="nav-item" active-class="nav-item--active">
          <span class="nav-icon">⚙️</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Settings</span>
        </router-link>
        <router-link v-if="auth.isAdmin" to="/admin" class="nav-item" active-class="nav-item--active">
          <span class="nav-icon">🛡️</span>
          <span class="nav-label" v-show="ui.isDesktop || ui.mobileNavOpen">Administration</span>
        </router-link>
      </nav>

      <div class="sidebar-section" v-show="ui.isDesktop || ui.mobileNavOpen">
        <h3 class="sidebar-section-title">Recent Pages</h3>
        <div v-if="loading" class="sidebar-loading">Loading...</div>
        <ul v-else class="sidebar-page-list">
          <li v-for="page in recentPages" :key="page.path">
            <router-link
              :to="`/wiki/${page.path}`"
              class="sidebar-page-link"
              :title="page.path"
            >
              {{ page.title || page.path }}
            </router-link>
          </li>
          <li v-if="recentPages.length === 0 && !loading" class="sidebar-empty">
            No pages yet
          </li>
        </ul>
      </div>

      <div class="sidebar-footer" v-show="ui.isMobile">
        <div v-if="auth.isAuthenticated" class="sidebar-user">
          <span class="sidebar-username">{{ auth.username }}</span>
          <button @click="handleLogout" class="sidebar-logout-btn">Logout</button>
        </div>
      </div>
    </aside>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUiStore } from '../stores/ui'
import { useAuthStore } from '../stores/auth'
import { authFetch } from '../utils/api'
import Logo from './Logo.vue'

const ui = useUiStore()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const recentPages = ref<Array<{ path: string; title?: string }>>([])
const loading = ref(true)

const fetchRecent = async () => {
  try {
    const res = await authFetch('/api/recent')
    if (res.ok) {
      const data = await res.json()
      recentPages.value = (data.pages || []).map((p: any) => ({
        path: p.path.replace(/^\//, ''),
        title: p.title,
      }))
    }
  } catch {
    // silent
  } finally {
    loading.value = false
  }
}

// Re-fetch recent pages on route change so edits/new pages appear immediately
watch(() => route.path, fetchRecent)

const handleLogout = async () => {
  try {
    await authFetch('/api/auth/logout', { method: 'POST' })
  } catch {
    // proceed
  }
  auth.logout()
  router.push('/login')
}

const onSidebarClick = (e: MouseEvent) => {
  if (ui.isMobile) {
    const target = e.target as HTMLElement
    if (target.closest('a')) {
      ui.mobileNavOpen = false
    }
  }
}

onMounted(fetchRecent)
</script>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  background-color: var(--color-surface);
  border-right: 1px solid var(--color-border);
  height: 100vh;
  position: sticky;
  top: 0;
  z-index: 200;
  overflow-y: auto;
  transition: width var(--transition-base), transform var(--transition-base);
  width: var(--sidebar-width);
  flex-shrink: 0;
}

.sidebar--collapsed {
  width: 56px;
  overflow: hidden;
}

.sidebar--mobile {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 300;
  box-shadow: var(--shadow-lg);
}

/* Backdrop for mobile */
.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 299;
}

/* Header */
.sidebar-header {
  display: flex;
  align-items: center;
  padding: var(--space-md);
  border-bottom: 1px solid var(--color-border);
  height: var(--header-height);
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
  color: var(--color-text);
}

.sidebar-title {
  font-weight: 700;
  font-size: var(--font-size-lg);
  white-space: nowrap;
}

/* Navigation */
.sidebar-nav {
  display: flex;
  flex-direction: column;
  padding: var(--space-sm);
  gap: 2px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.nav-item:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.nav-item--active {
  background: var(--color-primary);
  color: var(--color-primary-text);
}

.nav-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.nav-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Section */
.sidebar-section {
  padding: var(--space-sm) var(--space-md);
  flex: 1;
  overflow-y: auto;
}

.sidebar-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
  margin: var(--space-md) 0 var(--space-sm);
}

.sidebar-page-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-page-link {
  display: block;
  padding: 6px var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-decoration: none;
  border-radius: var(--radius-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background var(--transition-fast), color var(--transition-fast);
}

.sidebar-page-link:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

.sidebar-loading,
.sidebar-empty {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  padding: var(--space-sm);
}

/* Footer */
.sidebar-footer {
  padding: var(--space-md);
  border-top: 1px solid var(--color-border);
}

.sidebar-user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
}

.sidebar-username {
  font-size: var(--font-size-sm);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-logout-btn {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  color: var(--color-text-secondary);
  white-space: nowrap;
  transition: background var(--transition-fast);
}

.sidebar-logout-btn:hover {
  background: var(--color-surface-hover);
  color: var(--color-danger);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-base);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform var(--transition-slow);
}
.slide-left-enter-from {
  transform: translateX(-100%);
}
.slide-left-leave-to {
  transform: translateX(-100%);
}
</style>
